import { useState } from "react";
import type {
  ClassificationResult,
  DropzoneFile,
  BuildingUnit,
  BuildingAttribute,
} from "../types";

interface UseFileAssignmentReturn {
  unitOverrides: Record<string, string | null>;
  unitPickerOpen: boolean;
  setUnitPickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedFileForUnitPicker: DropzoneFile | null;
  attributeOverrides: Record<string, number | null>;
  attributePickerOpen: boolean;
  setAttributePickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedFileForAttributePicker: DropzoneFile | null;
  getEffectiveUnitId: (file: DropzoneFile) => string | null;
  getUnitDisplayName: (unitId: string | null) => string | null;
  shouldShowUnitPicker: (file: DropzoneFile) => boolean;
  isUnitLevelLabel: (file: DropzoneFile) => boolean;
  handleUnitPickerConfirm: (unit: { id: string; boxNumber?: string } | null) => void;
  openUnitPicker: (file: DropzoneFile) => void;
  getEffectiveBuildingAttributeId: (file: DropzoneFile) => number | null;
  getAttributeDisplayName: (attributeId: number | null) => string | null;
  shouldShowAttributePicker: (file: DropzoneFile) => boolean;
  isAttributeLevelLabel: (file: DropzoneFile) => boolean;
  handleAttributePickerConfirm: (attribute: BuildingAttribute | null) => void;
  openAttributePicker: (file: DropzoneFile) => void;
}

export function useFileAssignment(
  classificationResults: Record<string, ClassificationResult>,
  buildingUnits: BuildingUnit[],
  buildingAttributes: BuildingAttribute[]
): UseFileAssignmentReturn {
  const [unitOverrides, setUnitOverrides] = useState<Record<string, string | null>>({});
  const [unitPickerOpen, setUnitPickerOpen] = useState(false);
  const [selectedFileForUnitPicker, setSelectedFileForUnitPicker] = useState<DropzoneFile | null>(null);

  const [attributeOverrides, setAttributeOverrides] = useState<Record<string, number | null>>({});
  const [attributePickerOpen, setAttributePickerOpen] = useState(false);
  const [selectedFileForAttributePicker, setSelectedFileForAttributePicker] = useState<DropzoneFile | null>(null);

  const getEffectiveUnitId = (file: DropzoneFile): string | null => {
    if (unitOverrides[file.id] !== undefined) {
      return unitOverrides[file.id];
    }
    const classification = classificationResults[file.id];
    if (classification?.detectedUnitId) {
      return classification.detectedUnitId;
    }
    return file.unitId;
  };

  const getUnitDisplayName = (unitId: string | null): string | null => {
    if (!unitId) return null;

    const unit = buildingUnits.find((u) => u.id === unitId);
    if (!unit) return unitId;

    if (unit.boxNumber) return `Bus ${unit.boxNumber}`;
    if (unit.referencePlus) return unit.referencePlus;
    if (unit.unitCustomAddress) return unit.unitCustomAddress;
    if (unit.floor) return `Verdieping ${unit.floor}`;
    return unitId;
  };

  const shouldShowUnitPicker = (file: DropzoneFile): boolean => {
    const classification = classificationResults[file.id];
    if (!classification) return false;
    return buildingUnits.length > 0;
  };

  const isUnitLevelLabel = (file: DropzoneFile): boolean => {
    return shouldShowUnitPicker(file);
  };

  const handleUnitPickerConfirm = (unit: { id: string; boxNumber?: string } | null) => {
    if (!selectedFileForUnitPicker) return;

    setUnitOverrides((prev) => ({
      ...prev,
      [selectedFileForUnitPicker.id]: unit?.id || null,
    }));

    setUnitPickerOpen(false);
    setSelectedFileForUnitPicker(null);
  };

  const openUnitPicker = (file: DropzoneFile) => {
    setSelectedFileForUnitPicker(file);
    setUnitPickerOpen(true);
  };

  const getEffectiveBuildingAttributeId = (file: DropzoneFile): number | null => {
    if (attributeOverrides[file.id] !== undefined) {
      return attributeOverrides[file.id];
    }
    const classification = classificationResults[file.id];
    if (classification?.detectedAttributeId) {
      return classification.detectedAttributeId;
    }
    return file.buildingAttributeId;
  };

  const getAttributeDisplayName = (attributeId: number | null): string | null => {
    if (!attributeId) return null;

    const attr = buildingAttributes.find((a) => a.id === attributeId);
    if (!attr) return `Attribuut #${attributeId}`;

    return attr.template?.category
      ? `${attr.name} (${attr.template.category})`
      : attr.name;
  };

  const shouldShowAttributePicker = (file: DropzoneFile): boolean => {
    const classification = classificationResults[file.id];
    if (!classification) return false;
    return buildingAttributes.length > 0;
  };

  const isAttributeLevelLabel = (file: DropzoneFile): boolean => {
    return shouldShowAttributePicker(file);
  };

  const handleAttributePickerConfirm = (attribute: BuildingAttribute | null) => {
    if (!selectedFileForAttributePicker) return;

    setAttributeOverrides((prev) => ({
      ...prev,
      [selectedFileForAttributePicker.id]: attribute?.id || null,
    }));

    setAttributePickerOpen(false);
    setSelectedFileForAttributePicker(null);
  };

  const openAttributePicker = (file: DropzoneFile) => {
    setSelectedFileForAttributePicker(file);
    setAttributePickerOpen(true);
  };

  return {
    unitOverrides,
    unitPickerOpen,
    setUnitPickerOpen,
    selectedFileForUnitPicker,
    attributeOverrides,
    attributePickerOpen,
    setAttributePickerOpen,
    selectedFileForAttributePicker,
    getEffectiveUnitId,
    getUnitDisplayName,
    shouldShowUnitPicker,
    isUnitLevelLabel,
    handleUnitPickerConfirm,
    openUnitPicker,
    getEffectiveBuildingAttributeId,
    getAttributeDisplayName,
    shouldShowAttributePicker,
    isAttributeLevelLabel,
    handleAttributePickerConfirm,
    openAttributePicker,
  };
}
